/* eslint-disable @typescript-eslint/naming-convention */
import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { JobResultInterface } from '../../../models/interface/jobresult.interface';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { faClock } from '@fortawesome/free-solid-svg-icons/faClock';
import { faEllipsisH } from '@fortawesome/free-solid-svg-icons/faEllipsisH';
import { faFileDownload } from '@fortawesome/free-solid-svg-icons/faFileDownload';
import { faFilePdf } from '@fortawesome/free-solid-svg-icons/faFilePdf';
import { faRedo } from '@fortawesome/free-solid-svg-icons/faRedo';
import { faTimes } from '@fortawesome/free-solid-svg-icons/faTimes';
import { AnswersheetInterface } from '../../../models/interface/answersheet.interface';
import { Download } from '../../../helpers/download/download';
import { catchError, delay, mergeMap, repeat, skip, take } from 'rxjs/operators';
import { of } from 'rxjs';
import { HelperService } from '../../../services/helper/helper.service';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { GetAllJobResultsOfUserPayload, QueueActionTypes } from '../../../store/queue/queue.action';
import { AnswersheetsStore } from '../../../store/answersheet/component-store/answersheet.store';
import { PaginationListComponent } from '../../utils/pagination-list/pagination-list.component';

export type DialogAnswersheetReportData = {
  param: { lesson_name: string };
  answersheet: AnswersheetInterface;
};

type ResponseState = 'DEFAULT' | 'LOADING' | 'SUCCESS' | 'FAILURE';

@Component({
  selector: 'app-dialog-answersheet-report',
  templateUrl: './dialog-answersheet-report.component.html',
  styleUrls: ['./dialog-answersheet-report.component.scss'],
  providers: [AnswersheetsStore],
})
export class DialogAnswersheetReportComponent implements OnInit, OnDestroy {
  @ViewChild('paginator') paginator: PaginationListComponent;

  // Icons
  faFilePdf = faFilePdf;
  faFileDownload = faFileDownload;
  faTimes = faTimes;
  faEllipsisH = faEllipsisH;
  faRedo = faRedo;
  faClock = faClock;

  public param = { lesson_name: '...' };
  public answersheet: AnswersheetInterface;

  public isLoading$: Observable<boolean>;

  public downloadPDFReportResponse = new BehaviorSubject<ResponseState>('DEFAULT');
  public download$: Observable<Download>;

  public jobresults$: Observable<JobResultInterface[]>;
  public pagedJobResults$: Observable<JobResultInterface[]>;
  public pagedJobResultsSubject = new BehaviorSubject<JobResultInterface[]>([]);

  private isLoadingSubject = new BehaviorSubject<boolean>(true);

  private jobresultsSubject = new BehaviorSubject<JobResultInterface[]>([]);

  private subscriptions: Subscription[] = [];

  private answersheetPDFReportResponse$: Observable<any>;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogAnswersheetReportData,
    private answersheetsStore: AnswersheetsStore,
    private actions$: Actions,
    private store: Store,
    private helperService: HelperService,
  ) {
    this.isLoading$ = this.isLoadingSubject.asObservable();
    this.jobresults$ = this.jobresultsSubject.asObservable();
    this.answersheetPDFReportResponse$ = this.answersheetsStore.answersheetPDFReportResponse$;
  }

  public get helper() {
    return this.helperService;
  }

  ngOnInit(): void {
    this.param = this.data.param;
    this.answersheet = this.data.answersheet;
    const poll = of({}).pipe(
      mergeMap(() => {
        if (this.jobresultsSubject.value.some((job) => job.attributes.finished === 0)) {
          this.loadJobResults();
        }
        return of({});
      }),
      catchError(() => of(null)),
      delay(30000),
      repeat(),
    );
    this.subscriptions.push(poll.subscribe());
    this.pagedJobResults$ = this.pagedJobResultsSubject.asObservable();
    this.loadJobResults();
  }

  public getAnswersheetReportPDF(answersheet: AnswersheetInterface) {
    const downloadResponse = this.downloadPDFReportResponse.value;
    if (downloadResponse === 'FAILURE' || downloadResponse === 'SUCCESS') {
      this.downloadPDFReportResponse.next('DEFAULT');
      return;
    }
    if (downloadResponse !== 'DEFAULT') {
      return;
    }
    this.downloadPDFReportResponse.next('LOADING');
    this.answersheetsStore.getAnswersheetPDFReportQueue(answersheet.id);
    this.answersheetPDFReportResponse$
      .pipe(skip(1), take(1))
      .subscribe({
        next: () => {
          this.loadJobResults();
          this.downloadPDFReportResponse.next('SUCCESS');
          setTimeout(() => {
            this.downloadPDFReportResponse.next('DEFAULT');
          }, 2500);
        },
        error: () => {
          this.downloadPDFReportResponse.next('FAILURE');
          setTimeout(() => {
            this.downloadPDFReportResponse.next('DEFAULT');
          }, 2500);
        },
      });
  }

  public updatePagedJobResults(event: JobResultInterface[]) {
    if (event) {
      this.pagedJobResultsSubject.next(event);
    }
  }

  public loadJobResults(): void {
    const payload: GetAllJobResultsOfUserPayload = {
      jobType: 'Answersheet',
      entityId: this.answersheet.id,
    };
    this.store.dispatch({
      type: QueueActionTypes.getAllJobResultsOfUserType,
      payload,
    });
    this.subscriptions.push(
      this.actions$
        .pipe(
          ofType(
            QueueActionTypes.getAllJobResultsOfUserSuccessType,
            QueueActionTypes.getAllJobResultsOfUserErrorType,
          ),
          take(1),
        )
        .subscribe((result: any) => {
          if (result.type === QueueActionTypes.getAllJobResultsOfUserSuccessType) {
            const jobs: JobResultInterface[] = result['response']['body']['data'];
            const sortedJobs = jobs.sort(this.helper.sortByIdDesc);
            this.jobresultsSubject.next(sortedJobs);
            this.pagedJobResultsSubject.next(this.jobresultsSubject.value.slice(0, 20));
            this.paginator?.paginator?.firstPage();
            this.isLoadingSubject.next(false);
          }
        }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
